<script setup>
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { ref, defineProps, defineEmits } from 'vue';

const store = useStore();
const route = useRoute();

const props = defineProps({ choiceToUpdate: { type: Object, required: true } });
const emit = defineEmits(['update']);
const choice = ref({ ...props.choiceToUpdate });

const saveChoice = () => {
  const updatedChoice =
    choice.value.type === 'number'
      ? {
          name: choice.value.name,
          type: choice.value.type,
          default: choice.value.default,
          min: choice.value.min,
          max: choice.value.max,
          behaviors: choice.value.behaviors,
        }
      : {
          name: choice.value.name,
          type: choice.value.type,
          default: choice.value.default,
          behaviors: choice.value.behaviors,
        };
  store.commit('ci/setPreviewerOperation', {
    operations: [
      {
        component: 'choices',
        operation: 'PUT',
        details: {
          choice: updatedChoice,
        },
      },
    ],
    ciId: route.params.ciId,
  });
  emit('update', { ...choice.value });
};

const choiceTypeUpdated = () => {
  choice.value = {
    name: choice.value.name,
    type: choice.value.type,
    behaviors: choice.value.behaviors,
    min: choice.value.min,
    max: choice.value.max,
  };
};
</script>

<template>
  <form class="edit-choice" @submit.prevent="saveChoice">
    <div class="flex flex-col gap-1">
      <label> Name <abbr title="required">*</abbr></label>
      <input v-model="choice.name" placeholder="Choice Name" required type="text" name="choice-name" />
    </div>

    <div class="flex flex-col gap-1">
      <label> Choice Type <abbr title="required">*</abbr></label>
      <select
        v-model="choice.type"
        class="block h-10 w-full rounded-lg border border-gray-100 bg-white px-4 text-gray-800"
        required
        name="choice-type"
        @change="choiceTypeUpdated"
      >
        <option value="select">Select</option>
        <option value="color">Color</option>
        <option value="number">Number</option>
        <option value="string">String</option>
        <option value="boolean">Boolean</option>
        <option value="event">Event</option>
      </select>
    </div>

    <div class="flex flex-col gap-1">
      <label> Default </label>
      <input
        v-model="choice.default"
        placeholder="Choice Name"
        :type="{ color: 'color', number: 'number', boolean: 'checkbox' }[choice.type] || 'text'"
        name="choice-default"
      />
    </div>

    <div v-if="choice.type === 'select'" class="flex flex-col gap-1">
      <label for="">Options (Separated by New Line)</label>
      <textarea
        name="options"
        rows="5"
        :value="choice.options?.join('\n') || ''"
        @change="choice.options = $event.target.value.split('\n')"
      ></textarea>
    </div>

    <div v-if="choice.type === 'number'" class="flex gap-4">
      <div class="flex w-1/2 flex-col gap-1">
        <label> Minimum </label>
        <input v-model="choice.min" placeholder="Minimum" type="number" name="min" />
      </div>
      <div class="flex w-1/2 flex-col gap-1">
        <label> Maximum </label>
        <input v-model="choice.max" placeholder="Maximum" type="number" name="max" />
      </div>
    </div>

    <input value="Save" type="submit" />
  </form>
</template>

<style lang="postcss" scoped>
.edit-choice {
  @apply col-span-3 flex flex-col gap-4 p-4;
}

label {
  @apply font-semibold text-gray-600;
}

input[type='text'],
input[type='number'],
textarea,
select {
  @apply outline-none block w-full rounded-lg border border-gray-100 bg-white px-4 text-gray-800;
}
input[type='text'],
input[type='number'],
select {
  @apply h-10;
}
input[type='color'] {
  @apply outline-none mx-1 block h-8 w-8 rounded-lg border-none;
}

input[type='submit'] {
  @apply mb-2 ml-auto cursor-pointer rounded-full border-none bg-brand-500 px-5 py-2 text-sm
         font-medium tracking-wider text-white shadow-sm;
}
input[type='submit']:disabled {
  @apply cursor-not-allowed opacity-25;
}
input[type='submit']:hover {
  @apply bg-brand-600 shadow-lg;
}
</style>
